import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogTitle, DialogContent,
  Typography, TextField, IconButton, CircularProgress, Snackbar, Grid, Chip, Divider, Link
} from '@material-ui/core';
import MUIDataTable from "mui-datatables";
import { Cloud, Info } from '@material-ui/icons';
import { Checkbox, FormControlLabel } from '@material-ui/core';
const API_URL = "https://aitool-46a4c0ff8a02.herokuapp.com";

// --- Format time with +2h offset (PL)
function formatLocalTime(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString.replace(' ', 'T') + 'Z');
  return d.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' }).replace(',', '');
}

// --- Extract root domain for developer page
function getRootDomain(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    return u.protocol + '//' + u.hostname;
  } catch {
    return '';
  }
}

function formatRating(rating) {
    if (!rating) return '-';
    return Number(rating).toFixed(2);
  }

// --- Formularz tworzenia batcha
function BatchForm({ onCreated }) {
  const [batchName, setBatchName] = useState('');
  const [appInput, setAppInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ignoreAuto, setIgnoreAuto] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!batchName.trim() || !appInput.trim()) return;
    const appList = appInput
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map(bundle => ({
        bundle,
        platform: /^\d+$/.test(bundle) ? 'ios' : 'android'
      }));
    if (!appList.length) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/analyzeBatch_bg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_name: batchName, apps: appList, ignoreBlockedAndAutoapproved: ignoreAuto })
      });
      await resp.json();
      setLoading(false);
      setBatchName('');
      setAppInput('');
      onCreated && onCreated();
    } catch (err) {
      setLoading(false);
      alert("Błąd podczas tworzenia batcha!");
    }
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Create New Batch
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Batch Name"
            value={batchName}
            onChange={e => setBatchName(e.target.value)}
            required
            size="small"
            style={{ marginBottom: 16, marginRight: 16, width: 240 }}
          />
          <TextField
            label="App IDs / Bundles (one per line)"
            value={appInput}
            onChange={e => setAppInput(e.target.value)}
            required
            multiline
            minRows={5}
            placeholder={`com.example.app\n1234567890\ncom.foo.bar`}
            fullWidth
            style={{ marginBottom: 16 }}
          />
          <FormControlLabel
  control={
    <Checkbox
      checked={ignoreAuto}
      onChange={e => setIgnoreAuto(e.target.checked)}
      color="primary"
    />
  }
  label="Ignore autoapproved & blocked apps (treat all as normal)"
  style={{ marginBottom: 12 }}
/>
          <Button
            variant="contained"
            type="submit"
            endIcon={loading ? <CircularProgress size={18} /> : <Cloud />}
            disabled={loading}
          >
            Start Batch
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// --- Batch history jako tabelka z filtrowaniem
function BatchList({ onSelect }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progressMap, setProgressMap] = useState({});
  const fetchBatches = () => {
    setLoading(true);
    fetch(`${API_URL}/batches`)
      .then(r => r.json())
      .then(async (data) => {
        setBatches(data || []);
        const progressData = {};
        for (const batch of data) {
          try {
            const res = await fetch(`${API_URL}/batch_progress/${batch.id}`);
            const prog = await res.json();
            progressData[batch.id] = prog;
          } catch {
            progressData[batch.id] = { total: 0, done: 0 };
          }
        }
        setProgressMap(progressData);
        setLoading(false);
      })
      .catch(() => { setBatches([]); setLoading(false); });
  };

  useEffect(() => { fetchBatches(); }, []);

  const sorted = [...batches].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  const columns = [
    { name: 'id', label: 'ID', options: { filter: false, sort: true } },
    { name: 'name', label: 'Name', options: { filter: true, sort: true } },
    {
      name: 'created_at',
      label: 'Created At',
      options: {
        filter: false, sort: true,
        customBodyRender: value => formatLocalTime(value)
      }
    },
    {
      name: 'progress',
      label: 'Progress',
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const id = batches[tableMeta.rowIndex]?.id;
          const prog = progressMap[id] || { total: 0, done: 0 };
          return prog.total ? `${prog.done}/${prog.total} (${Math.round((prog.done / prog.total) * 100)}%)` : '-';
        }
      }
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onSelect(sorted[tableMeta.rowIndex].id)}
          >Show</Button>
        )
      }
    }
  ];

  return (
    <Card style={{ marginBottom: 24 }}>
      <CardContent>
        <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
          Batch History
          <Button onClick={fetchBatches} size="small" style={{ float: "right" }}>Refresh</Button>
        </Typography>
        <MUIDataTable
          title={false}
          data={sorted}
          columns={columns}
          options={{
            filter: true,
            selectableRows: "none",
            responsive: "standard",
            rowsPerPage: 10,
            textLabels: {
              body: { noMatch: loading ? "Loading..." : "No data" }
            }
          }}
        />
      </CardContent>
    </Card>
  );
}

// --- Procentowy progress batcha
function useBatchProgress(batchId) {
  const [progress, setProgress] = useState({ total: 0, done: 0 });

  useEffect(() => {
    if (!batchId) return;
    fetch(`${API_URL}/batch_progress/${batchId}`)
      .then(r => r.json())
      .then(setProgress)
      .catch(() => setProgress({ total: 0, done: 0 }));
  }, [batchId]);
  return progress;
}

// --- Tabela appek z batcha
function BatchAppsTable({ batchId }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const progress = useBatchProgress(batchId);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    fetch(`${API_URL}/batch/${batchId}`)
      .then(r => r.json())
      .then(data => {
        setApps(data.apps || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [batchId]);

  const columns = [
    { name: 'app_id', label: 'App ID' },
    { name: 'name', label: 'Name' },
    {
      name: 'appStoreUrl',
      label: 'Store URL',
      options: {
        filter: false,
        customBodyRender: (value) =>
          value ? <a href={value} target="_blank" rel="noopener noreferrer">Store</a> : ""
      }
    },
    { name: 'platform', label: 'Platform' },
    { name: 'averageRating', label: 'Avg. Rating' },
    { name: 'reviewCount', label: 'Reviews' },
    { name: 'installs', label: 'Installs' },
    { name: 'blocked', label: 'Blocked' },
    { name: 'isMonetized', label: 'Monetized' },
    { name: 'monetizedBy', label: 'Monetized By' },
    { name: 'ratingDE', label: 'ratingDE' },
    { name: 'ratingES', label: 'ratingES' },
    { name: 'ratingFR', label: 'ratingFR' },
    { name: 'ratingIT', label: 'ratingIT' },
    { name: 'ratingPL', label: 'ratingPL' },
    { name: 'approvalStatus', label: 'Approval Status' },
    { name: 'reason', label: 'Reason for Result' },
    { name: 'score', label: 'Score' },
    {
      name: 'details',
      label: '',
      options: {
        filter: false,
        customBodyRender: (value, tableMeta, updateValue) => (
          <IconButton onClick={() => setSelected(apps[tableMeta.rowIndex])}>
            <Info />
          </IconButton>
        )
      }
    }
  ];

  const data = (apps || []).map(app => {
    let res = {};
    try {
      res = app.result_json ? (typeof app.result_json === 'string' ? JSON.parse(app.result_json) : app.result_json) : {};
    } catch { res = {}; }
    let devUrl = '';
    let rootDomain = '';
    if (app.platform === 'ios') {
      devUrl = res.sellerUrl || '';
      rootDomain = getRootDomain(devUrl);
    } else if (app.platform === 'android') {
      devUrl = res.developerWebsite || '';
      rootDomain = getRootDomain(devUrl);
    }
    return {
      app_id: app.app_id,
      name: res.name || '',
      appStoreUrl: res.shopurl || res.appStoreUrl || '',
      platform: app.platform,
      averageRating: res.rating || res.averageRating || '',
      reviewCount: res.reviewCount || '',
      installs: (app.platform === 'android')
  ? (res.fullData?.installs || res.installs || '-')
  : '-',
      developerPage: rootDomain,
      appAdsTxt: rootDomain ? (rootDomain + '/app-ads.txt') : '',
      averageCountryRating: res.averageCountryRating || '',
      blocked: res.isBlocked ? 'Yes' : 'No',
      isMonetized: res.isMonetized ? 'Yes' : 'No',
      monetizedBy: res.monetizedBy ?? '',
      ratingDE: res.ratingDE ?? '',
      ratingES: res.ratingES ?? '',
      ratingFR: res.ratingFR ?? '',
      ratingIT: res.ratingIT ?? '',
      ratingPL: res.ratingPL ?? '',
      approvalStatus: res.approvalStatus ?? (app.status === "pending" ? "Pending" : ''),
      reason: res.reason ?? '',
      score: res.score ?? '',
      details: '',
      result_full: res,
      result_row: app
    }
  });

  return (
    <Card style={{ marginTop: 16 }}>
      <CardContent>
        <Typography variant="h6">Analysis Results</Typography>
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          Done: {progress.done} / {progress.total}
          {progress.total > 0 ? (
            <span> ({Math.round((progress.done / progress.total) * 100)}%)</span>
          ) : null}
          {" "}
          <span style={{ color: '#aaa', fontSize: 13 }}>Refresh batch to see the updated progress</span>
        </Typography>
        <div style={{ width: "100%", marginTop: 12 }}>
          <MUIDataTable
            title={false}
            data={data}
            columns={columns}
            options={{
              filter: true,
              selectableRows: "none",
              responsive: "standard",
              rowsPerPage: 10,
              onRowClick: (rowData, rowMeta) => setSelected(apps[rowMeta.dataIndex]),
              textLabels: {
                body: { noMatch: loading ? "Loading..." : "No data" }
              }
            }}
          />
        </div>
        <AppDetailsModal
  open={!!selected}
  app={selected}
  onClose={() => setSelected(null)}
/>
      </CardContent>
    </Card>
  );
}

function getScreenshots(app) {
    // iOS i Android mają różne miejsca
    if (!app) return [];
    // Najpierw iOS
    if (app.fullData?.screenshotUrls && Array.isArray(app.fullData.screenshotUrls) && app.fullData.screenshotUrls.length > 0) {
      return app.fullData.screenshotUrls;
    }
    // iPad screenshots (też iOS)
    if (app.fullData?.ipadScreenshotUrls && Array.isArray(app.fullData.ipadScreenshotUrls) && app.fullData.ipadScreenshotUrls.length > 0) {
      return app.fullData.ipadScreenshotUrls;
    }
    // Android
    if (app.fullData?.screenshots && Array.isArray(app.fullData.screenshots) && app.fullData.screenshots.length > 0) {
      return app.fullData.screenshots;
    }
    // Legacy (nie powinno być)
    if (Array.isArray(app.screenshotUrls) && app.screenshotUrls.length > 0) {
      return app.screenshotUrls;
    }
    if (Array.isArray(app.screenshots) && app.screenshots.length > 0) {
      return app.screenshots;
    }
    return [];
  }
// --- Modal szczegółów appki (ładnie, ze screenshotami, developer page/app-ads.txt)
function AppDetailsModal({ open, app, onClose }) {
    const [zoomImg, setZoomImg] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [brandSafe, setBrandSafe] = useState(undefined);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [reviewsError, setReviewsError] = useState('');
    const [showReviews, setShowReviews] = useState(false);

    const handleFetchReviews = async () => {
        if (!app) return;
        setReviews([]);
        setBrandSafe(undefined);
        setReviewsLoading(true);
        setReviewsError('');
        setShowReviews(true);
      
        let res = {};
        try {
          res = app.result_json
            ? typeof app.result_json === 'string'
              ? JSON.parse(app.result_json)
              : app.result_json
            : {};
        } catch {
          res = {};
        }
        const bundle = app.app_id || res.id || res.bundleId;
        let platform =
          app.platform ||
          res.platform ||
          (/^\d+$/.test(bundle) ? 'apple' : 'google');
        // Mapuj na apple/google
        if (platform.toLowerCase() === 'ios') platform = 'apple';
        if (platform.toLowerCase() === 'android') platform = 'google';
      
        try {
          const resp = await fetch(`${API_URL}/analyzeApp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bundle, platform }),
          });
          if (!resp.ok) throw new Error('Cannot fetch reviews');
          const data = await resp.json();
          setReviews(data.reviews || []);
          setBrandSafe(data.brand_safe ?? undefined);
        } catch (e) {
          setReviewsError('Error loading reviews');
        } finally {
          setReviewsLoading(false);
        }
      };
    
      useEffect(() => {
        if (open) {
          setShowReviews(false);
          setReviews([]);
          setBrandSafe(undefined);
          setReviewsError('');
          setReviewsLoading(false);
        }
      }, [open, app]);

    if (!app) return null;
    // obsłuż pusty result_json
    let res = {};
    try { res = app.result_json ? (typeof app.result_json === 'string' ? JSON.parse(app.result_json) : app.result_json) : {}; } catch { res = {}; }
  
    // Platforma, ID itd
    const platform = app.platform || res.platform;
    const app_id = app.app_id || res.id || res.bundleId;
    const name = res.name || app.name || '';
    const primaryGenre = res.primaryGenre || res.genre || '';
    const iconUrl = res.iconUrl || res.imageUrl || res.fullData?.artworkUrl100 || '';
    
    // Developer Page
    let devUrl = '';
    let rootDomain = '';
    if (platform === 'ios') {
      devUrl = res.sellerUrl || res.fullData?.sellerUrl || '';
      rootDomain = getRootDomain(devUrl);
    } else if (platform === 'android') {
      devUrl = res.developerWebsite || res.fullData?.developerWebsite || '';
      rootDomain = getRootDomain(devUrl);
    }
    const appAdsTxtUrl = rootDomain ? rootDomain + '/app-ads.txt' : '';
    
    // Screenshots (najpierw iOS, potem Android, potem fallback)
    let screenshots =
      (res.fullData?.screenshotUrls?.length && res.fullData.screenshotUrls) ||
      (res.fullData?.ipadScreenshotUrls?.length && res.fullData.ipadScreenshotUrls) ||
      (res.fullData?.screenshots?.length && res.fullData.screenshots) ||
      (res.screenshotUrls?.length && res.screenshotUrls) ||
      (res.screenshots?.length && res.screenshots) ||
      [];
  
    // Remove falsy (np. undefined w tablicy)
    screenshots = Array.isArray(screenshots) ? screenshots.filter(Boolean) : [];
  
    // Reszta pól
    const averageRating = res.averageRating ?? res.rating ?? '-';
    const reviewCount = res.reviewCount ?? res.reviews ?? '-';
    const ratingDE = res.ratingDE ?? '-';
    const ratingES = res.ratingES ?? '-';
    const ratingFR = res.ratingFR ?? '-';
    const ratingIT = res.ratingIT ?? '-';
    const ratingPL = res.ratingPL ?? '-';
    const approvalStatus = res.approvalStatus ?? '-';
    const reason = res.reason ?? '';
    const score = res.score ?? '-';
    const isBlocked = res.isBlocked ? "Blocked" : "Not Blocked";
    const isMonetized = res.isMonetized ? "Monetized" : "Not Monetized";
    const monetizedBy = res.monetizedBy ? `Monetized By: ${res.monetizedBy}` : null;
  
    return (
        <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {iconUrl && (
              <img src={iconUrl} alt="icon"
                style={{ width: 56, height: 56, borderRadius: 12, background: "#eee", objectFit: "cover", marginRight: 16 }}
              />
            )}
            <Box>
              <Typography variant="h6">{name}</Typography>
              <Typography style={{ color: '#888', fontSize: 14 }}>{primaryGenre}</Typography>
            </Box>
            <Box flexGrow={1} />
            <Link href={res.appStoreUrl || res.shopurl || "#"} target="_blank" rel="noopener" underline="none">
              <Chip label="Store Page" color="primary" />
            </Link>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ paddingTop: 24 }}>
          <Grid container spacing={2}>
            {/* --- LEFT (details) --- */}
            <Grid item xs={12} md={7}>
              <Box>
              <Divider style={{ margin: "12px 0 12px 0" }} />
                <Box mb={1}>
                  <Typography variant="body2" color="textSecondary">Average Rating</Typography>
                  <Typography>
                    <b>{formatRating(averageRating)}</b>
                    {" "}({reviewCount} reviews)
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleFetchReviews}
                    disabled={reviewsLoading}
                  >
                    {reviewsLoading ? <CircularProgress size={18} /> : "Show Reviews"}
                  </Button>
                  {brandSafe !== undefined && (
                    <Chip
                      label={`Brand Safe: ${brandSafe ? "Yes" : "No"}`}
                      style={{
                        background: brandSafe ? "#27ae60" : "#eb4d4b",
                        color: "#fff",
                        marginLeft: 12,
                        fontWeight: 600,
                        fontSize: 14
                      }}
                    />
                  )}

{showReviews && (
                  <Box mt={1}>
                    {reviewsLoading && <Typography color="textSecondary">Loading reviews...</Typography>}
                    {reviewsError && <Typography color="error">{reviewsError}</Typography>}
                    {!reviewsLoading && reviews && reviews.length > 0 && (
                      <Box style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
                        {reviews.map((review, idx) => (
                          <Typography key={idx} style={{ fontSize: 15, marginBottom: 10, color: "#444", whiteSpace: 'pre-line' }}>
                            {review}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {!reviewsLoading && (!reviews || reviews.length === 0) && !reviewsError && showReviews && (
                      <Typography color="textSecondary">No reviews found.</Typography>
                    )}
                  </Box>
                )}
              
                </Box>
                
                <Box mb={1}>
                  <Typography variant="body2" color="textSecondary">Country Ratings</Typography>
                  <Box style={{ fontSize: 13, color: "#444" }}>
                    DE: {formatRating(ratingDE)}{" | "}
                    ES: {formatRating(ratingES)}{" | "}
                    FR: {formatRating(ratingFR)}{" | "}
                    IT: {formatRating(ratingIT)}{" | "}
                    PL: {formatRating(ratingPL)}
                  </Box>
                </Box>
                <Divider style={{ margin: "12px 0 12px 0" }} />
                <Box mb={1}>
                  <Typography variant="body2" color="textSecondary">Status & Scoring</Typography>
                  <div>
                    <Chip size="small"
                      label={approvalStatus}
                      style={{
                        background: approvalStatus === "Approved" ? "#27ae60" : approvalStatus === "Manual check" ? "#f6e58d" : "#eb4d4b",
                        color: "#fff",
                        marginRight: 8
                      }}
                    />
                    <Chip size="small"
                      label={`Score: ${score !== undefined ? score : "-"}`}
                      style={{ background: "#0984e3", color: "#fff" }}
                    />
                  </div>
                  {reason && (
                    <Typography style={{ fontSize: 13, marginTop: 6, color: "#999" }}>
                      {reason}
                    </Typography>
                  )}
                </Box>
                <Divider style={{ margin: "12px 0 12px 0" }} />
                <Box mb={1} display="flex" flexWrap="wrap" gap={2}>
                  <Chip
                    label={isBlocked}
                    color={isBlocked === "Blocked" ? "secondary" : "default"}
                    size="small"
                  />
                  <Chip
                    label={isMonetized}
                    color={isMonetized === "Monetized" ? "primary" : "default"}
                    size="small"
                  />
                  {monetizedBy && (
                    <Chip
                      label={monetizedBy}
                      color="default"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
                <Box mb={1}>
                  <Typography variant="body2" color="textSecondary">Developer</Typography>
                  <Typography>
                    {res.artistName || res.sellerName || res.developerName || "-"}
                    {rootDomain && (
                      <>
                        {" "}
                        <Link href={rootDomain} target="_blank" rel="noopener" underline="hover" style={{ fontSize: 13 }}>
                          ({rootDomain.replace(/^https?:\/\//, '')})
                        </Link>
                      </>
                    )}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2" color="textSecondary">App ID</Typography>
                  <Typography>{app_id}</Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2" color="textSecondary">App-ads.txt</Typography>
                  {appAdsTxtUrl
                    ? <Link href={appAdsTxtUrl} target="_blank" rel="noopener">{appAdsTxtUrl}</Link>
                    : <span>-</span>
                  }
                </Box>
                <Box mb={1}>
  <Typography variant="body2" color="textSecondary">Description</Typography>
  <Typography style={{
    fontSize: 15,
    whiteSpace: "pre-line",
    color: "#444",
    marginTop: 2,
    marginBottom: 4
  }}>
    {res.fullData?.description || res.fullData?.summary || res.description || "-"}
  </Typography>
</Box>

            </Grid>
            {/* --- RIGHT (screenshots) --- */}
            <Grid item xs={12} md={5}>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>Screenshots</Typography>
                {screenshots.length > 0 ? (
                  <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                    {screenshots.slice(0, 6).map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`screen${i}`}
                        style={{ width: 120, borderRadius: 8, cursor: 'pointer' }}
                        loading="lazy"
                        onClick={() => setZoomImg(url)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="textSecondary">No screenshots</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {/* Dialog powiększonego obrazka */}
      <Dialog open={!!zoomImg} onClose={() => setZoomImg(null)} maxWidth="lg" >
      <DialogContent
  style={{
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    overflow: 'auto',         // <-- dodaj przewijanie
    maxHeight: '80vh'         // <-- ogranicz max wysokość okna dialogu
  }}
>
    <img
      src={zoomImg}
      alt="Zoomed screenshot"
      style={{
        maxWidth: '80vw',
        maxHeight: '80vh',
        objectFit: 'contain',
        borderRadius: 8,
        boxShadow: '0 0 15px rgba(0,0,0,0.5)',
      }}
    />
  </DialogContent>
</Dialog>
      </>
    );
  }

// --- Główny komponent
export default function AppAnalyser3() {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [snack, setSnack] = useState("");

  return (
    <Box style={{ width: "85vw", minHeight: "100vh", padding: 0, margin: 0 }}>
      <BatchForm onCreated={() => { setSelectedBatch(null); setSnack("Batch created!"); }} />
      <BatchList onSelect={id => setSelectedBatch(id)} />
      {selectedBatch &&
        <BatchAppsTable batchId={selectedBatch} />
      }
      <Snackbar open={!!snack} autoHideDuration={1800} onClose={() => setSnack("")} message={snack} />
    </Box>
  );
}
